import type { GeoJsonObject, Position } from 'geojson';
import { LatLngBounds, geoJSON } from 'leaflet';
import { Marker, Popup } from 'react-leaflet';
import cloneDeep from 'lodash/cloneDeep';
import Map from '../Shared/Map';
import './style.scss';
import useFetch, { Status } from '../../hooks/fetch.hook';
import { StopAreaApiModel } from '../StopAreas/models/stop-area.api-model';
import type { IStopArea } from '../../models/stop-area.model';
import { ButtonDivIcon } from '../Shared/Map/markers/button-div-icon';
import { TranslationService } from '../../services/translation.service';
import { MarkerHelper } from '../../helpers/marker.helper';
import Colors from '../../helpers/colors.helper';

function processReceivedStopAreaData(data: StopAreaApiModel[] | undefined): IStopArea[] | undefined {
  return data?.map(StopAreaApiModel.mapToBusinessModel) ?? undefined;
}

function getBoundsFromMultiPolygon(polygon: GeoJSON.MultiPolygon | undefined): LatLngBounds | undefined {
  if (!polygon) return undefined;

  return geoJSON(polygon).getBounds();
}

function getInvertedMunicipalityPolygon(polygon: GeoJSON.MultiPolygon): GeoJSON.MultiPolygon | undefined {
  if (!polygon || !polygon.coordinates) return polygon;

  const world: Position[] = [
    [90, 180],
    [90, -180],
    [-90, -180],
    [-90, 180],
  ];

  const coordinatesWithOuterShape = cloneDeep(polygon.coordinates);
  coordinatesWithOuterShape[0].unshift(world); // https://leafletjs.com/reference.html#polygon

  const invertedPolygon: GeoJSON.MultiPolygon = { type: 'MultiPolygon', coordinates: coordinatesWithOuterShape };

  return invertedPolygon;
}

function LocalServiceMap({ gid }: { gid: string }) {
  // === STATE ===
  const [{ status: getLocalServiceGeoJsonStatus, data: getLocalServiceGeoJsonData }] = useFetch<GeoJsonObject[]>(`/api/local-service/excluded-areas/${gid}`);
  const [{ status: getMunicipalityGeoJsonStatus, data: getMunicipalityGeoJsonData }] = useFetch<GeoJSON.MultiPolygon>(`/api/local-service/municipality/${gid}`);
  const [{ data: getStopAreasData }] = useFetch<StopAreaApiModel[]>(`/api/local-service/stopareas/${gid}`);

  // === VARIABLES ===
  const bounds = getBoundsFromMultiPolygon(getMunicipalityGeoJsonData);
  const stopAreas = processReceivedStopAreaData(getStopAreasData);
  const colorMarkerBlue = '#006C93';

  const geoJsons =
    getLocalServiceGeoJsonData && getMunicipalityGeoJsonData && getLocalServiceGeoJsonStatus === Status.success && getMunicipalityGeoJsonStatus === Status.success
      ? getLocalServiceGeoJsonData
          .map((geoJsonData, index) => ({
            id: `local-service-${index}`,
            data: geoJsonData,
            options: { color: Colors.vtPink06, fillColor: Colors.vtPink06, fillOpacity: 0.35, weight: 2, interactive: false },
          }))
          .concat({
            id: 'municipality',
            data: getInvertedMunicipalityPolygon(getMunicipalityGeoJsonData) as GeoJsonObject,
            options: { color: Colors.vtPink06, fillColor: Colors.vtdGray700, fillOpacity: 0.35, weight: 2, interactive: false },
          })
      : undefined;

  const circleMarkerSvg = MarkerHelper.getCircleMarkerSvg(8, 4, Colors.vtdWhite, colorMarkerBlue);

  return (
    <div>
      <h3>{TranslationService.get('common.localservicemapblock.map')}</h3>
      <dl>
        <div className="d-flex mb-1">
          <dt className="local-service-map__description-term local-service-map__description-term--area">
            <div className="w-100 h-100"></div>
          </dt>
          <dd className="ms-2 mb-0">{TranslationService.get('common.localservicemapblock.areawithoutlocalservice')}</dd>
        </div>
        <div className="d-flex">
          <dt className="local-service-map__description-term d-flex align-items-center justify-content-center" dangerouslySetInnerHTML={{ __html: circleMarkerSvg.svg }}></dt>
          <dd className="ms-2 mb-0">{TranslationService.get('common.localservicemapblock.localservicestoparea')}</dd>
        </div>
      </dl>
      <Map
        ariaLabel={TranslationService.get('common.localservicemapblock.map')}
        className="local-service-map__map"
        geoJsons={geoJsons}
        bounds={bounds ? { bounds, method: 'fitBounds' } : undefined}
        markers={stopAreas
          ?.map((x) =>
            x.latitude !== undefined && x.longitude !== undefined ? (
              <Marker
                position={[x.latitude, x.longitude]}
                key={x.gid}
                icon={
                  new ButtonDivIcon({
                    className: 'button-div-icon stop-area-marker',
                    iconSize: [circleMarkerSvg.width, circleMarkerSvg.height],
                    iconAnchor: [circleMarkerSvg.width * 0.5, circleMarkerSvg.height * 0.5],
                    html: `<div><div class='visually-hidden'>${TranslationService.get('common.stop')} ${x.name}</div>${circleMarkerSvg.svg}</div>`,
                  })
                }
              >
                <Popup>{`${x.name}, ${TranslationService.get('common.platform')} ${x.stopPoints.find((y) => y.isFlexibleTaxiService)?.platform}`}</Popup>
              </Marker>
            ) : null
          )
          .filter((marker): marker is React.ReactElement => marker !== null)}
      />
    </div>
  );
}

export default LocalServiceMap;
